<template>
  <ul
    :class="cn('flex flex-col gap-1', props.class)"
    role="list"
    :aria-label="ariaLabel"
  >
    <li v-for="item in items" :key="item.key" class="nav-list__li">
      <component
        :is="resolveTag(item)"
        v-bind="resolveTagAttrs(item)"
        :class="itemClass(item)"
        :aria-current="isActive(item) ? 'page' : null"
        :aria-disabled="item.disabled || null"
        :data-active="isActive(item) ? '' : null"
        :data-variant="item.iconVariant || null"
        @click="onClickItem($event, item)"
        @keydown.enter="onClickItem($event, item)"
        @keydown.space.prevent="onClickItem($event, item)"
      >
        <i class="nav-list__icon not-italic">
          <component
            v-if="isIconComponent(item.icon)"
            :is="item.icon"
            class="h-[18px] w-[18px]"
          />
          <SvgIcon
            v-else-if="item.icon"
            :name="item.icon"
            class="h-[18px] w-[18px] fill-none"
          />
        </i>

        <i class="nav-list__label not-italic">{{ item.label }}</i>

        <output v-if="formatCount(item.count)" class="nav-list__count">
          {{ formatCount(item.count) }}
        </output>
        <output v-else-if="item.badge" class="nav-list__count">
          {{ item.badge }}
        </output>
      </component>
    </li>
  </ul>
</template>

<script setup>
import { SvgIcon } from "@/shared/ui/svg-icon";
import { cn } from "@/shared/lib/utils";

/**
 * Reusable vertical navigation list.
 *
 * Items shape:
 *   {
 *     key:          string                              required, used for activeKey + v-for key
 *     label:        string                              visible text
 *     icon:         string | VueComponent | null        svg-icon name OR a Vue component (e.g. lucide)
 *     iconVariant:  "default" | "ai" | "calendar" | "meet" | "todo" | "critical" | "reports"
 *                                                       optional, picks an icon color scheme
 *     count:        number | null                       optional unread counter
 *     badge:        string | null                       optional pre-formatted text badge
 *     to:           RouteLocationRaw | null             optional <router-link> target
 *     href:         string | null                       optional <a href>
 *     disabled:     boolean
 *   }
 */
const props = defineProps({
  items: { type: Array, required: true },
  activeKey: { type: [String, Number, null], default: null },
  class: { type: [String, Array, Object], default: "" },
  ariaLabel: { type: String, default: undefined },
});

const emit = defineEmits(["select"]);

function isActive(item) {
  return props.activeKey != null && item.key === props.activeKey;
}

function isIconComponent(icon) {
  return (
    icon &&
    typeof icon !== "string" &&
    (typeof icon === "object" || typeof icon === "function")
  );
}

function resolveTag(item) {
  if (item.disabled) return "button";
  if (item.to) return "router-link";
  if (item.href) return "a";
  return "button";
}

function resolveTagAttrs(item) {
  if (item.disabled) return { type: "button", disabled: true };
  if (item.to) return { to: item.to };
  if (item.href) return { href: item.href };
  return { type: "button" };
}

function onClickItem(event, item) {
  if (item.disabled) {
    event.preventDefault?.();
    return;
  }
  emit("select", item.key, item, event);
}

function itemClass(item) {
  return [
    "nav-list__link",
    isActive(item) ? "is-active" : "",
    item.disabled ? "is-disabled" : "",
  ];
}

function formatCount(n) {
  if (n === null || n === undefined || n === "" || n === 0) return "";
  if (typeof n !== "number") return String(n);
  return n > 999 ? "999+" : String(n);
}
</script>

<style lang="scss" scoped>
.nav-list__link {
  @apply flex w-full items-center gap-2 rounded-md border border-transparent
    px-2 py-1.5 text-left text-sm transition-colors;
  color: hsl(var(--sidebar-foreground));
  text-decoration: none;
  background-color: transparent;

  &:hover:not(.is-disabled):not(.is-active) {
    background-color: hsl(var(--muted));
    color: hsl(var(--sidebar-accent-foreground));
  }

  &.is-active {
    @apply border-border;
    background-color: rgb(var(--color-white));
    color: rgb(var(--color-blue-500));

    .nav-list__icon :deep(svg) {
      color: rgb(var(--color-blue-500));
    }
  }

  &.is-disabled {
    @apply cursor-not-allowed opacity-50;
  }
}

.nav-list__icon {
  @apply inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center;
  color: hsl(var(--sidebar-foreground));

  :deep(svg) {
    @apply h-[18px] w-[18px];
    color: inherit;
  }
}

.nav-list__label {
  @apply flex-1 truncate text-sm;
}

.nav-list__count {
  @apply ml-auto inline-flex h-5 min-w-[22px] items-center justify-center
    rounded-md px-1.5 text-xs;
  background-color: hsl(var(--sidebar-accent));
  color: hsl(var(--sidebar-accent-foreground));
}

/* Per-variant icon coloring — mirrors NavBar.vue patterns. */
.nav-list__link[data-variant="ai"] .nav-list__icon :deep(svg) {
  color: rgb(var(--color-purple-700));
  fill: rgb(var(--color-purple-50));
}
.nav-list__link[data-variant="calendar"] .nav-list__icon :deep(svg) {
  color: rgb(var(--color-blue-400));
  fill: rgb(var(--color-blue-50));
}
.nav-list__link[data-variant="meet"] .nav-list__icon :deep(svg) {
  color: rgb(var(--color-red-600));
  fill: rgb(var(--color-red-300));
}
.nav-list__link[data-variant="todo"] .nav-list__icon :deep(svg) {
  color: rgb(var(--color-green-700));
  fill: rgb(var(--color-green-100));
}
.nav-list__link[data-variant="critical"] .nav-list__icon :deep(svg) {
  color: rgb(var(--color-red-600));
  fill: rgb(var(--color-red-50));
}
.nav-list__link[data-variant="reports"] .nav-list__icon :deep(svg) {
  color: rgb(var(--color-amber-600));
  fill: rgb(var(--color-amber-50));
}
.nav-list__link[data-variant="signing"] .nav-list__icon :deep(svg) {
  color: rgb(var(--color-emerald-600));
  fill: rgb(var(--color-emerald-50));
}
</style>
